import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { concatWith, Subscription, take } from 'rxjs';
import { UserService } from 'src/app/core/api/user/user.service';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { RegisterService } from 'src/app/core/services/register/register.service';
import { BusinessDataFormObject } from 'src/app/shared/models/business-data/business-data-form-object/business-data-form-object';
import { BusinessEquipmentFormObject } from 'src/app/shared/models/business-equipment/business-equipment-form-object/business-equipment-form-object';
import { BusinessRepresentativeFormObject } from 'src/app/shared/models/business-representative/business-representative-form-object/business-representative-form-object';
import { RepresentativeUpdateRequestDTO } from 'src/app/shared/models/dtos/requests/representative-update-request-dto/representative-update-request-dto';
import { Routes } from '../../routes';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { RoutesKeys } from 'src/app/utils/routes';

@Component({
  selector: 'app-confirm-data',
  templateUrl: './confirm-data.component.html',
  styleUrls: ['./confirm-data.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * ConfirmDataComponent!
 */
export class ConfirmDataComponent implements OnInit, OnDestroy {
  carouselPages: string[] = ['page-1', 'page-2', 'page-3'];
  termsFormGroup = new FormGroup({
    agree: new FormControl(false, [Validators.requiredTrue]),
  });
  recaptchaFormGroup = new FormGroup({
    recaptcha: new FormControl('', Validators.required),
  });
  businessData!: BusinessDataFormObject;
  equipmentData!: BusinessEquipmentFormObject;
  businessRepresentative!: BusinessRepresentativeFormObject;
  creatingAccount = false;
  errorMessage = '';
  private subsink = new Subscription();
  equipmentDataLoading = true;
  businessId?: number;
  errorMessages = {
    businessIdCantBeNull: 'business-id-cant-be-null',
    businessIdUnavailable: 'business-id-unavailable',
  };

  /**
   * @constructor
   */
  constructor(
    private readonly registerService: RegisterService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly authenticatedUserService: AuthenticatedUserService,
    private readonly userService: UserService,
    private readonly translationService: TranslationService
  ) {}

  /**
   * on init
   */
  ngOnInit() {
    if (!this.authenticatedUserService.user) {
      this.router.navigate([RoutesKeys.ONBOARD]);
    } else {
      this.translationService.translateObject(this.errorMessages);
      this.registerService.resetForms(true);
      this.businessData = this.registerService.getBusinessDataFormObject();
      this.businessData.formGroup.disable();
      this.businessData.formGroup.addControl(
        'ready',
        new FormControl(false, Validators.requiredTrue)
      );
      this.equipmentData =
        this.registerService.getBusinessEquipmentFormObject();
      this.equipmentData.formGroup.disable();
      this.businessRepresentative =
        this.registerService.businessRepresentativeFormObject;
      this.businessRepresentative.formGroup.disable();

      this.registerService
        .getTermsOfUseAndPrivacyConditionsURLs()
        .pipe(take(1))
        .subscribe({
          next: (res: any) => {
            this.businessRepresentative.termsAndConditionsLink =
              res.termsOfUseURL;
            this.businessRepresentative.privacyPolicyLink =
              res.privacyConditionsURL;
          },
          error: () => {
            this.termsFormGroup.disable();
          },
        });

      this.authenticatedUserService.getBusinessId().then((businessId) => {
        if (businessId != null) {
          this.businessId = businessId;
          this.updateEquipmentData(businessId);
          this.updateBusinessData(businessId);
        } else {
          throw new Error(this.errorMessages.businessIdCantBeNull);
        }
      });
      this.subsink.add(
        this.userService
          .getRepresentativeInfo()
          .subscribe((rep) =>
            this.fillRepresentativeData(
              rep!.email,
              rep!.email,
              rep!.firstName,
              rep!.lastName,
              rep!.picture
            )
          )
      );
    }
  }
  /**
   * Cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
    this.registerService.resetForms();
  }

  /**
   * Fills representative data
   * @param {string} email
   * @param {string} confirmEmail
   * @param {string} name
   * @param {string} lastName
   * @param {string} picture
   */
  fillRepresentativeData(
    email: string,
    confirmEmail: string,
    name: string,
    lastName: string,
    picture: string
  ) {
    this.businessRepresentative.formGroup.setValue({
      email,
      confirmEmail,
      name,
      lastName,
      picture,
      password: '',
      confirmPassword: '',
    });
    this.businessRepresentative.formGroup.enable();
    this.businessRepresentative.formGroup.controls['email'].disable();
  }

  /**
   * Pulls business data from API
   * @param {number} businessId
   */
  async updateBusinessData(businessId: number) {
    this.businessData = this.registerService.getBusinessDataFormObject(
      businessId,
      () => {
        this.businessData.formGroup.controls['ready'].setValue(true);
      }
    );
  }

  /**
   * Pulls equipment data from API
   * @param {number} businessId
   */
  async updateEquipmentData(businessId: number) {
    this.equipmentDataLoading = true;
    this.equipmentData = this.registerService.getBusinessEquipmentFormObject(
      businessId,
      () => {
        this.equipmentDataLoading = false;
      }
    );
  }

  /**
   * Confirms data
   */
  confirmData() {
    if (!this.businessId) {
      throw new Error(this.errorMessages.businessIdUnavailable);
    }
    const businessRepresentativeValues =
      this.businessRepresentative.formGroup.getRawValue();

    const representative = new RepresentativeUpdateRequestDTO(
      businessRepresentativeValues.name,
      businessRepresentativeValues.lastName,
      businessRepresentativeValues.email,
      businessRepresentativeValues.picture || '',
      this.authenticatedUserService.representative!.language
    );
    const business = this.registerService.businessDataDTOFromForm();

    let { recaptcha } = this.recaptchaFormGroup.getRawValue();
    recaptcha = recaptcha || '';

    this.userService
      .updateRepresentative(representative, recaptcha)
      .pipe(concatWith(this.userService.updateBusinessData(business)))
      .subscribe(() => {
        this.authenticatedUserService.resetRepresentative();
        this.router.navigate([`../${Routes.ONBOARDING}`], {
          relativeTo: this.activatedRoute,
        });
      });
  }
}
